import { FormEvent, useEffect, useState } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import Loader from '../../loader/Loader';
import Modal from '../../modal/Modal';
import FilterDropdown, { FilterOption } from '../../custom-input/FilterDropdown';
import ValidatedInput, { ValidatedState } from '../../custom-input/ValidatedInput';
import { AlertSVG } from '../../svgs';
import { BankAccountHolderType, StripeDateOfBirth, StripeKycDetails } from '../../../types/interfaces/stripeConnect.interfaces';
import { ActiveAccount } from '../../../types/interfaces/account.interfaces';
import { StripePayoutSetupMode, Validators } from '../../../types/enums';
import {
  createStripeAccount,
  doesStripeAccountExist,
  getStripeAccountStatus,
  submitStripeKyc,
  updateStripeAccount,
} from '../../../api/stripeConnectApi';
import useStripeKycDetails from '../../../hooks/useStripeKycDetails';
import useCanManagePayouts from '../../../hooks/useCanManagePayouts';
import { showToast } from '../../../services/toast.service';
import { createBankAccountToken } from '../../../services/stripeBankAccount.service';
import { maskExceptLast4 } from '../../../utils/commonUtils';
import { FormErrors, toastMessages } from '../../../constants/errorMessages';
import { COUNTRIES_WITH_STATES, COUNTRY_CODES } from '../../../constants/data';
import { ADDRESS_CONSTANTS, BUTTON_CONSTANTS, LABEL_CONSTANTS, PAYMENT_CONSTANTS } from '../../../constants/common';
import './stripePayoutSetupModal.scss';

type StripePayoutSetupModalProps = {
  isOpen: boolean;
  accountId: number;
  mode?: StripePayoutSetupMode;
  description?: string;
  onClose: () => void;
  onSuccess: () => void;
};

const requiredState = (value = ''): ValidatedState => ({
  value,
  valid: value.trim().length > 0,
});

const emailState = (value = ''): ValidatedState => ({
  value,
  valid: /^\S+@\S+\.\S+[^.]$/.test(value.trim()),
});

const formError = (validator: Validators) => FormErrors.find(error => error.validator === validator);

const formErrorMessage = (validator: Validators) => formError(validator)?.message || toastMessages.SOMETHING_WENT_WRONG;

const matchesFormError = (validator: Validators, value: string) => formError(validator)?.condition?.test(value.trim()) ?? false;

const stripeKycErrorMessage = (title?: string) => {
  if (!title) return toastMessages.SOMETHING_WENT_WRONG;
  if (/stripe api error on '(contact_phone|phone)'/i.test(title)) {
    return toastMessages.STRIPE_PHONE_NUMBER_INVALID;
  }
  const requestPrefix = 'Error occurred while processing request:';
  const withoutRequestPrefix = title.startsWith(requestPrefix) ? title.slice(requestPrefix.length).trim() : title.trim();
  const message = withoutRequestPrefix.replace(/^Stripe API error on '[^']+':\s*/, '');
  return message || toastMessages.SOMETHING_WENT_WRONG;
};

const STRIPE_PHONE_COUNTRY_CODE = '+1';

const localPhoneDigits = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits;
};

const toStripePhone = (value: string): string => `${STRIPE_PHONE_COUNTRY_CODE}${localPhoneDigits(value)}`;

const phoneState = (value = ''): ValidatedState => {
  const phoneValue = localPhoneDigits(value);
  return {
    value: phoneValue,
    valid: matchesFormError(Validators.STRIPE_KYC_PHONE, phoneValue),
  };
};

const postalCodeState = (value = ''): ValidatedState => ({
  value,
  valid: /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value.trim()),
});

const optionalState = (value = ''): ValidatedState => ({
  value,
  valid: true,
});

const holderTypeOptions: FilterOption<BankAccountHolderType>[] = [
  { name: PAYMENT_CONSTANTS.INDIVIDUAL, value: 'individual' },
  { name: PAYMENT_CONSTANTS.COMPANY, value: 'company' },
];

const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const oldestDateOfBirth = '1900-01-01';

const latestAdultDateOfBirth = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 18);
  return formatDateInput(date);
};

const parseDateOfBirth = (value: string): StripeDateOfBirth | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  if (year < 1900 || year > new Date().getFullYear()) return null;

  const eighteenthBirthday = new Date(year + 18, month - 1, day);
  if (eighteenthBirthday > new Date()) return null;

  return { day, month, year };
};

const dateOfBirthMessage = (value: string) => {
  if (!value.trim()) return toastMessages.STRIPE_KYC_DATE_OF_BIRTH_REQUIRED;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return toastMessages.STRIPE_KYC_DATE_OF_BIRTH_INVALID;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  const isRealDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  if (!isRealDate || year < 1900 || year > new Date().getFullYear() || month < 1 || month > 12 || day < 1 || day > 31) {
    return toastMessages.STRIPE_KYC_DATE_OF_BIRTH_INVALID;
  }

  const eighteenthBirthday = new Date(year + 18, month - 1, day);
  if (eighteenthBirthday > new Date()) return toastMessages.STRIPE_KYC_MINIMUM_AGE;

  return '';
};

const matchStateOption = (raw?: string): FilterOption<string> => {
  if (!raw) return { name: '', value: '' };
  const match = (COUNTRIES_WITH_STATES.US ?? []).find(
    state => state.value.toLowerCase() === raw.toLowerCase() || state.name.toLowerCase() === raw.toLowerCase(),
  );
  return match ?? { name: raw, value: raw };
};

type KycFormPatch = {
  firstName: ValidatedState;
  lastName: ValidatedState;
  email: ValidatedState;
  phone: ValidatedState;
  line1: ValidatedState;
  line2: ValidatedState;
  city: ValidatedState;
  stateOption: FilterOption<string>;
  postalCode: ValidatedState;
};

const withValue = <T,>(value: T | undefined, include: boolean): T | undefined => (include ? value : undefined);

const mapKycDetailsToForm = (details: StripeKycDetails): Partial<KycFormPatch> => {
  const address = details.address;

  return {
    firstName: withValue(requiredState(details.firstName ?? ''), Boolean(details.firstName)),
    lastName: withValue(requiredState(details.lastName ?? ''), Boolean(details.lastName)),
    email: withValue(emailState(details.email ?? ''), Boolean(details.email)),
    phone: withValue(phoneState(details.phone ?? ''), Boolean(details.phone)),
    line1: withValue(requiredState(address?.line1 ?? ''), Boolean(address?.line1)),
    line2: withValue(optionalState(address?.line2 ?? ''), Boolean(address?.line2)),
    city: withValue(requiredState(address?.city ?? ''), Boolean(address?.city)),
    stateOption: withValue(matchStateOption(address?.state), Boolean(address?.state)),
    postalCode: withValue(postalCodeState(address?.postalCode ?? ''), Boolean(address?.postalCode)),
  };
};

const applyKycFormPatch = (
  patch: Partial<KycFormPatch>,
  setters: { [Key in keyof KycFormPatch]: (value: KycFormPatch[Key]) => void },
) => {
  (Object.keys(patch) as (keyof KycFormPatch)[]).forEach(key => {
    const value = patch[key];
    if (value !== undefined) setters[key](value as never);
  });
};

const StripePayoutSetupModal = ({
  isOpen,
  accountId,
  mode = StripePayoutSetupMode.Kyc,
  description,
  onClose,
  onSuccess,
}: StripePayoutSetupModalProps) => {
  const user = useAppSelector(state => state.user);
  const activeAccount = useAppSelector(state => state.activeAccount) as ActiveAccount;
  const prefilledAddress = activeAccount.addresses?.[0] ?? user.addresses?.[0];
  const prefilledEmail = activeAccount.contactEmail || (user.username?.includes('@') ? user.username : '');
  const prefilledName = `${user.firstName || activeAccount.contactFirstName || ''} ${
    user.lastName || activeAccount.contactLastName || ''
  }`.trim();

  const [firstName, setFirstName] = useState<ValidatedState>(() =>
    requiredState(user.firstName || activeAccount.contactFirstName || ''),
  );
  const [lastName, setLastName] = useState<ValidatedState>(() =>
    requiredState(user.lastName || activeAccount.contactLastName || ''),
  );
  const [email, setEmail] = useState<ValidatedState>(() => emailState(prefilledEmail));
  const [phone, setPhone] = useState<ValidatedState>(() => phoneState(user.contactPhone || activeAccount.contactPhone || ''));
  const setPhoneValue = (state: ValidatedState) => setPhone(phoneState(state.value));
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [idNumberLast4, setIdNumberLast4] = useState<ValidatedState>(requiredState());
  const [line1, setLine1] = useState<ValidatedState>(() => requiredState(prefilledAddress?.streetAddress || ''));
  const [line2, setLine2] = useState<ValidatedState>(() => optionalState(prefilledAddress?.streetAddress2 || ''));
  const [city, setCity] = useState<ValidatedState>(() => requiredState(prefilledAddress?.city || ''));
  const [stateOption, setStateOption] = useState<FilterOption<string>>(() => matchStateOption(prefilledAddress?.state));
  const [postalCode, setPostalCode] = useState<ValidatedState>(() => postalCodeState(prefilledAddress?.postalCode || ''));
  const [accountHolderName, setAccountHolderName] = useState<ValidatedState>(() => requiredState(prefilledName));
  const [accountHolderType, setAccountHolderType] = useState<FilterOption<BankAccountHolderType>>(holderTypeOptions[0]);
  const [routingNumber, setRoutingNumber] = useState<ValidatedState>(requiredState());
  const [accountNumber, setAccountNumber] = useState<ValidatedState>(requiredState());
  const [confirmAccountNumber, setConfirmAccountNumber] = useState<ValidatedState>(requiredState());
  const [validate, setValidate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [fullIdNumber, setFullIdNumber] = useState<ValidatedState>(requiredState());
  const [requiresFullIdNumber, setRequiresFullIdNumber] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const requiresTermsAcceptance = mode !== StripePayoutSetupMode.Edit;
  const canManagePayouts = useCanManagePayouts();
  const { kycDetails, errorMessage } = useStripeKycDetails(canManagePayouts ? accountId : undefined);

  useEffect(() => {
    if (!isOpen || !accountId) return;
    let isActive = true;
    const loadFullIdNumberRequirement = async () => {
      try {
        const { data: accountExists } = await doesStripeAccountExist(accountId);
        if (accountExists !== true) return;
        const { data: status } = await getStripeAccountStatus(accountId);
        if (isActive) setRequiresFullIdNumber(status?.requiresFullIdNumber === true);
      } catch {
      }
    };
    void loadFullIdNumberRequirement();
    return () => {
      isActive = false;
    };
  }, [isOpen, accountId]);

  useEffect(() => {
    if (!kycDetails) return;

    applyKycFormPatch(mapKycDetailsToForm(kycDetails), {
      firstName: setFirstName,
      lastName: setLastName,
      email: setEmail,
      phone: setPhoneValue,
      line1: setLine1,
      line2: setLine2,
      city: setCity,
      stateOption: setStateOption,
      postalCode: setPostalCode,
    });
  }, [kycDetails]);

  useEffect(() => {
    if (!errorMessage) return;
    showToast.error(stripeKycErrorMessage(errorMessage));
  }, [errorMessage]);

  const parsedDateOfBirth = parseDateOfBirth(dateOfBirth);
  const country = COUNTRY_CODES[0];

  const identityValidationMessage = () => {
    if (!firstName.value.trim()) return toastMessages.STRIPE_KYC_FIRST_NAME_REQUIRED;
    if (!lastName.value.trim()) return toastMessages.STRIPE_KYC_LAST_NAME_REQUIRED;
    if (!email.value.includes('@')) return toastMessages.STRIPE_KYC_EMAIL_REQUIRED;
    if (!phone.value.trim()) return toastMessages.STRIPE_KYC_PHONE_REQUIRED;
    if (!matchesFormError(Validators.STRIPE_KYC_PHONE, phone.value)) return formErrorMessage(Validators.STRIPE_KYC_PHONE);
    const birthDateMessage = dateOfBirthMessage(dateOfBirth);
    if (birthDateMessage) return birthDateMessage;
    if (requiresFullIdNumber) {
      if (!/^\d{9}$/.test(fullIdNumber.value.replace(/[-\s]/g, ''))) {
        return fullIdNumber.value.trim() ? toastMessages.STRIPE_KYC_SSN_FULL : toastMessages.STRIPE_KYC_SSN_REQUIRED;
      }
    } else if (!/^\d{4}$/.test(idNumberLast4.value.trim())) {
      return idNumberLast4.value.trim() ? toastMessages.STRIPE_KYC_SSN_LAST_4 : toastMessages.STRIPE_KYC_SSN_REQUIRED;
    }
    if (!line1.value.trim()) return toastMessages.STRIPE_KYC_ADDRESS_LINE_1_REQUIRED;
    if (!city.value.trim()) return toastMessages.STRIPE_KYC_CITY_REQUIRED;
    if (!stateOption.value) return toastMessages.STRIPE_KYC_STATE_REQUIRED;
    if (!postalCode.valid) return toastMessages.STRIPE_KYC_POSTAL_CODE_REQUIRED;
    return '';
  };

  const isBankValid =
    accountHolderName.valid &&
    Boolean(accountHolderType.value) &&
    routingNumber.valid &&
    accountNumber.valid &&
    confirmAccountNumber.valid &&
    accountNumber.value === confirmAccountNumber.value;

  const goToPaymentDetails = () => {
    setValidate(true);
    const validationMessage = identityValidationMessage();
    if (validationMessage) {
      showToast.error(validationMessage);
      return;
    }
    setValidate(false);
    setStep(2);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (step === 1) {
      goToPaymentDetails();
      return;
    }

    setValidate(true);
    const validationMessage = identityValidationMessage();
    if (validationMessage) {
      showToast.error(validationMessage);
      return;
    }
    if (!isBankValid || isSubmitting) {
      if (!isBankValid) {
        const bankMessage = !accountHolderName.value.trim()
          ? formErrorMessage(Validators.REQUIRED)
          : !routingNumber.valid
          ? formErrorMessage(Validators.ROUTING_NUMBER)
          : accountNumber.value !== confirmAccountNumber.value
          ? formErrorMessage(Validators.MUST_MATCH)
          : formErrorMessage(Validators.ACCOUNT_NUMBER);
        showToast.error(bankMessage);
      }
      return;
    }
    if (!parsedDateOfBirth) return;
    if (requiresTermsAcceptance && !acceptedTerms) {
      showToast.error(toastMessages.STRIPE_KYC_TERMS_REQUIRED);
      return;
    }

    try {
      setIsSubmitting(true);
      const bankAccountToken = await createBankAccountToken({
        accountHolderName: accountHolderName.value.trim(),
        accountHolderType: accountHolderType.value,
        routingNumber: routingNumber.value,
        accountNumber: accountNumber.value,
      });

      const payoutRequest = {
        accountId,
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        email: email.value.trim(),
        phone: toStripePhone(phone.value),
        dateOfBirth: parsedDateOfBirth,
        address: {
          line1: line1.value.trim(),
          line2: line2.value.trim(),
          city: city.value.trim(),
          state: stateOption.value,
          postalCode: postalCode.value.trim(),
          country: country.value,
        },
        idNumberLast4: requiresFullIdNumber ? '' : idNumberLast4.value,
        idNumber: requiresFullIdNumber ? fullIdNumber.value.replace(/[-\s]/g, '') : undefined,
        acceptTermsOfService: requiresTermsAcceptance && acceptedTerms,
        bankAccountToken,
        maskedBankRoutingNumber: maskExceptLast4(routingNumber.value),
        maskedAccountNumber: maskExceptLast4(accountNumber.value),
      };

      if (mode === StripePayoutSetupMode.Edit) {
        await updateStripeAccount(accountId, payoutRequest);
      } else {
        const { data: accountExists } = await doesStripeAccountExist(accountId);
        if (accountExists !== true) {
          await createStripeAccount(accountId);
        }
        await submitStripeKyc(payoutRequest);
      }

      showToast.success(toastMessages.STRIPE_PAYOUT_SETUP_SUCCESS);
      onSuccess();
    } catch (error: any) {
      const clientMessage = typeof error?.message === 'string' ? error.message : '';
      if (
        clientMessage === toastMessages.STRIPE_ACCOUNT_NUMBER_INVALID ||
        clientMessage === toastMessages.STRIPE_ROUTING_NUMBER_INVALID ||
        clientMessage === toastMessages.STRIPE_PUBLISHABLE_KEY_MISSING
      ) {
        showToast.error(clientMessage);
        setIsSubmitting(false);
        return;
      }
      const title = error?.response?.status === 404 ? undefined : error?.response?.data?.title;
      showToast.error(stripeKycErrorMessage(title));
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} ignoreBackdrop width={760}>
      <form className="stripe-payout-setup-modal" onSubmit={onSubmit}>
        {isSubmitting && <Loader loaderSize="small" pageLoader />}
        <h2>{mode === StripePayoutSetupMode.Edit ? LABEL_CONSTANTS.UPDATE_PAYMENT_METHOD : LABEL_CONSTANTS.KYC_ONBOARDING}</h2>
        <p>{description || LABEL_CONSTANTS.STRIPE_ONBOARDING_REDIRECT}</p>
        <div className="step-counter">
          <div className={step === 1 ? 'step-bubble active' : 'step-bubble'}>1</div>
          <div className="step-connector" />
          <div className={step === 2 ? 'step-bubble active' : 'step-bubble'}>2</div>
        </div>

        {step === 1 && (
          <>
            <h3>{PAYMENT_CONSTANTS.PERSONAL_INFORMATION}</h3>
            <div className="input-row">
              <div className="input">
                <ValidatedInput
                  label={`*${LABEL_CONSTANTS.FIRST_NAME}`}
                  type="text"
                  useId="stripe-payout-first-name"
                  validatedStateForAutoFill={firstName}
                  setValidatedState={setFirstName}
                  validators={[Validators.REQUIRED]}
                  validate={validate}
                />
              </div>
              <div className="input">
                <ValidatedInput
                  label={`*${LABEL_CONSTANTS.LAST_NAME}`}
                  type="text"
                  useId="stripe-payout-last-name"
                  validatedStateForAutoFill={lastName}
                  setValidatedState={setLastName}
                  validators={[Validators.REQUIRED]}
                  validate={validate}
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input">
                <ValidatedInput
                  label={`*${LABEL_CONSTANTS.EMAIL}`}
                  type="email"
                  useId="stripe-payout-email"
                  validatedStateForAutoFill={email}
                  setValidatedState={setEmail}
                  validators={[Validators.REQUIRED, Validators.EMAIL]}
                  validate={validate}
                />
              </div>
              <div className="input phone-field">
                <label className="validated-input-label" htmlFor="stripe-payout-phone">
                  *{LABEL_CONSTANTS.PHONE_NO}
                </label>
                <div className="phone-field-box">
                  <span className="phone-field-code">{STRIPE_PHONE_COUNTRY_CODE}</span>
                  <ValidatedInput
                    label=""
                    type="number"
                    useId="stripe-payout-phone"
                    placeholder="5555555555"
                    validatedStateForAutoFill={phone}
                    setValidatedState={setPhoneValue}
                    validators={[Validators.REQUIRED, Validators.STRIPE_KYC_PHONE]}
                    validate={validate}
                  />
                </div>
              </div>
            </div>
            <div className="input-row">
              <div className="input">
                <label className="validated-input-label" htmlFor="stripe-payout-dob">
                  *{PAYMENT_CONSTANTS.DATE_OF_BIRTH}
                  <div className="validated-input">
                    <input
                      id="stripe-payout-dob"
                      type="date"
                      value={dateOfBirth}
                      min={oldestDateOfBirth}
                      max={latestAdultDateOfBirth()}
                      onChange={event => setDateOfBirth(event.target.value)}
                    />
                  </div>
                </label>
                {validate && !parsedDateOfBirth && (
                  <div className="validated-input-message-error">
                    <AlertSVG /> {dateOfBirthMessage(dateOfBirth)}
                  </div>
                )}
              </div>
              <div className="input">
                {requiresFullIdNumber ? (
                  <>
                    <ValidatedInput
                      label={`*${LABEL_CONSTANTS.FULL_SSN}`}
                      type="text"
                      useId="stripe-payout-ssn-full"
                      maxLength={11}
                      validatedStateForAutoFill={fullIdNumber}
                      setValidatedState={setFullIdNumber}
                      validators={[Validators.REQUIRED]}
                      validate={validate}
                    />
                    <p className="ssn-full-help">{LABEL_CONSTANTS.STRIPE_FULL_SSN_REQUIRED_HELP}</p>
                  </>
                ) : (
                  <ValidatedInput
                    label={`*${PAYMENT_CONSTANTS.SSN_LAST_4}`}
                    type="number"
                    useId="stripe-payout-ssn"
                    maxLength={4}
                    validatedStateForAutoFill={idNumberLast4}
                    setValidatedState={setIdNumberLast4}
                    validators={[Validators.REQUIRED, Validators.LAST_FOUR_DIGITS]}
                    validate={validate}
                  />
                )}
              </div>
            </div>

            <h3>{ADDRESS_CONSTANTS.ADDRESS_INFO}</h3>
            <ValidatedInput
              label={`*${ADDRESS_CONSTANTS.ADDRESS_LINE_1}`}
              type="text"
              useId="stripe-payout-line1"
              validatedStateForAutoFill={line1}
              setValidatedState={setLine1}
              validators={[Validators.REQUIRED]}
              validate={validate}
            />
            <ValidatedInput
              label={ADDRESS_CONSTANTS.ADDRESS_LINE_2}
              type="text"
              useId="stripe-payout-line2"
              validatedStateForAutoFill={line2}
              setValidatedState={setLine2}
              validators={[]}
              validate={validate}
            />
            <div className="input-row">
              <div className="input">
                <ValidatedInput
                  label={`*${ADDRESS_CONSTANTS.CITY}`}
                  type="text"
                  useId="stripe-payout-city"
                  validatedStateForAutoFill={city}
                  setValidatedState={setCity}
                  validators={[Validators.REQUIRED]}
                  validate={validate}
                />
              </div>
              <div className="input">
                <label className="validated-input-label">{`*${ADDRESS_CONSTANTS.STATE}`}</label>
                <FilterDropdown
                  placeholder="Select"
                  options={COUNTRIES_WITH_STATES.US ?? []}
                  value={stateOption}
                  validate={validate}
                  onChange={setStateOption}
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input">
                <ValidatedInput
                  label={`*${ADDRESS_CONSTANTS.ZIP_CODE}`}
                  type="text"
                  useId="stripe-payout-postal"
                  validatedStateForAutoFill={postalCode}
                  setValidatedState={setPostalCode}
                  validators={[Validators.REQUIRED, Validators.ZIP_CODE]}
                  validate={validate}
                />
              </div>
              <div className="input">
                <label className="validated-input-label">{ADDRESS_CONSTANTS.COUNTRY}</label>
                <FilterDropdown options={COUNTRY_CODES} value={country} disabled onChange={() => undefined} />
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h3>{PAYMENT_CONSTANTS.PAYMENT_INFORMATION}</h3>
            <div className="input-row">
              <div className="input">
                <ValidatedInput
                  label={`*${PAYMENT_CONSTANTS.ACCOUNT_HOLDER_NAME}`}
                  type="text"
                  useId="stripe-payout-holder-name"
                  validatedStateForAutoFill={accountHolderName}
                  setValidatedState={setAccountHolderName}
                  validators={[Validators.REQUIRED]}
                  validate={validate}
                />
              </div>
              <div className="input">
                <label className="validated-input-label">{`*${PAYMENT_CONSTANTS.ACCOUNT_HOLDER_TYPE}`}</label>
                <FilterDropdown
                  options={holderTypeOptions}
                  value={accountHolderType}
                  onChange={setAccountHolderType}
                  showSearch={false}
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input">
                <ValidatedInput
                  label={`*${PAYMENT_CONSTANTS.ROUTING_NUMBER}`}
                  type="number"
                  useId="stripe-payout-routing"
                  validatedStateForAutoFill={routingNumber}
                  setValidatedState={setRoutingNumber}
                  validators={[Validators.REQUIRED, Validators.ROUTING_NUMBER]}
                  validate={validate}
                />
              </div>
              <div className="input">
                <ValidatedInput
                  label={`*${PAYMENT_CONSTANTS.ACCOUNT_NUMBER}`}
                  type="number"
                  useId="stripe-payout-account"
                  validatedStateForAutoFill={accountNumber}
                  setValidatedState={setAccountNumber}
                  validators={[Validators.REQUIRED, Validators.ACCOUNT_NUMBER]}
                  validate={validate}
                  preventPaste
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input">
                <ValidatedInput
                  label={`*${PAYMENT_CONSTANTS.CONFIRM_ACCOUNT_NUMBER}`}
                  type="number"
                  useId="stripe-payout-confirm-account"
                  validatedStateForAutoFill={confirmAccountNumber}
                  setValidatedState={setConfirmAccountNumber}
                  matchString={accountNumber.value}
                  validators={[Validators.REQUIRED, Validators.ACCOUNT_NUMBER]}
                  validate={validate}
                  preventPaste
                />
              </div>
            </div>
            {requiresTermsAcceptance && (
              <div className="stripe-terms-agreement">
                <input
                  type="checkbox"
                  id="stripe-terms-agreement"
                  checked={acceptedTerms}
                  onChange={event => setAcceptedTerms(event.target.checked)}
                />
                <label htmlFor="stripe-terms-agreement">
                  {LABEL_CONSTANTS.STRIPE_TOS_AGREEMENT_PREFIX}
                  <a href={LABEL_CONSTANTS.STRIPE_TOS_AGREEMENT_URL} target="_blank" rel="noopener noreferrer">
                    {LABEL_CONSTANTS.STRIPE_TOS_AGREEMENT_LINK}
                  </a>
                  {LABEL_CONSTANTS.STRIPE_TOS_AGREEMENT_SUFFIX}
                </label>
              </div>
            )}
          </>
        )}

        <div className="flex-right">
          {step === 1 ? (
            <button type="button" className="button green inverted medium" onClick={onClose} disabled={isSubmitting}>
              {BUTTON_CONSTANTS.CANCEL}
            </button>
          ) : (
            <button type="button" className="button green inverted medium" onClick={() => setStep(1)} disabled={isSubmitting}>
              {BUTTON_CONSTANTS.BACK}
            </button>
          )}
          <button type="submit" className="button green medium" disabled={isSubmitting}>
            {step === 1 ? BUTTON_CONSTANTS.NEXT_STEP : BUTTON_CONSTANTS.SAVE}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default StripePayoutSetupModal;
